export const TOOL_CATEGORIES = [
  'core',
  'crm',
  'files',
  'finance',
  'general',
  'inventory',
  'purchases',
  'sales',
  'transportation',
] as const;
export type ToolCategory = (typeof TOOL_CATEGORIES)[number];

export interface ToolDefinition {
  readonly id: string;
  readonly category: ToolCategory;
}

export const AVAILABLE_TOOLS: readonly ToolDefinition[] = [
  // CRM tools
  { id: 'crm-account-list', category: 'crm' },
  { id: 'crm-account-get', category: 'crm' },
  { id: 'crm-account-create', category: 'crm' },
  { id: 'crm-contact-list', category: 'crm' },

  // Sales tools
  { id: 'sales-salesOrder-list', category: 'sales' },
  { id: 'sales-salesOrder-get', category: 'sales' },

  // Inventory tools
  { id: 'inventory-item-list', category: 'inventory' },
  { id: 'inventory-item-get', category: 'inventory' },

  // Core tools
  { id: 'core-customAttribute-create', category: 'core' },
  { id: 'core-customAttribute-delete', category: 'core' },
  { id: 'core-customAttribute-get', category: 'core' },
  { id: 'core-customAttribute-list', category: 'core' },
  { id: 'core-customAttribute-update', category: 'core' },
  { id: 'core-customAttribute-validate', category: 'core' },
  { id: 'core-facility-create', category: 'core' },
  { id: 'core-facility-get', category: 'core' },
  { id: 'core-facility-list', category: 'core' },
  { id: 'core-facilityGroup-assignFacility', category: 'core' },
  { id: 'core-facilityGroup-create', category: 'core' },
  { id: 'core-facilityGroup-get', category: 'core' },
  { id: 'core-facilityGroup-list', category: 'core' },
  { id: 'core-facilityGroup-removeFacility', category: 'core' },
  { id: 'core-facilityGroup-update', category: 'core' },
  { id: 'core-organization-create', category: 'core' },
  { id: 'core-organization-delete', category: 'core' },
  { id: 'core-organization-get', category: 'core' },
  { id: 'core-organization-list', category: 'core' },
  { id: 'core-organization-update', category: 'core' },
  { id: 'core-scopedSettingValues-getBySetting', category: 'core' },
  { id: 'core-scopedSettingValues-patch', category: 'core' },
  { id: 'core-setting-create', category: 'core' },
  { id: 'core-setting-delete', category: 'core' },
  { id: 'core-setting-get', category: 'core' },
  { id: 'core-setting-list', category: 'core' },
  { id: 'core-setting-update', category: 'core' },
  { id: 'core-settingsGroup-create', category: 'core' },
  { id: 'core-settingsGroup-delete', category: 'core' },
  { id: 'core-settingsGroup-get', category: 'core' },
  { id: 'core-settingsGroup-list', category: 'core' },
  { id: 'core-settingsGroup-update', category: 'core' },
  { id: 'core-user-create', category: 'core' },
  { id: 'core-user-get', category: 'core' },
  { id: 'core-user-list', category: 'core' },

  // Additional CRM tools
  { id: 'crm-account-delete', category: 'crm' },
  { id: 'crm-account-update', category: 'crm' },
  { id: 'crm-accountAddress-create', category: 'crm' },
  { id: 'crm-accountAddress-delete', category: 'crm' },
  { id: 'crm-accountAddress-get', category: 'crm' },
  { id: 'crm-accountAddress-list', category: 'crm' },
  { id: 'crm-accountAddress-update', category: 'crm' },
  { id: 'crm-contact-create', category: 'crm' },
  { id: 'crm-contact-delete', category: 'crm' },
  { id: 'crm-contact-get', category: 'crm' },
  { id: 'crm-contact-update', category: 'crm' },
  { id: 'crm-customerInvitation-accept', category: 'crm' },
  { id: 'crm-customerInvitation-cancel', category: 'crm' },
  { id: 'crm-customerInvitation-create', category: 'crm' },
  { id: 'crm-customerInvitation-get', category: 'crm' },
  { id: 'crm-customerInvitation-getByToken', category: 'crm' },
  { id: 'crm-customerInvitation-list', category: 'crm' },

  // Files tools
  { id: 'files-file-generateAccessURL', category: 'files' },
  { id: 'files-file-get', category: 'files' },
  { id: 'files-file-list', category: 'files' },
  { id: 'files-fileTemplate-get', category: 'files' },
  { id: 'files-fileTemplate-list', category: 'files' },
  { id: 'files-fileTemplate-set', category: 'files' },
  { id: 'files-fileTemplate-remove', category: 'files' },

  // Finance tools
  { id: 'finance-bill-create', category: 'finance' },
  { id: 'finance-bill-get', category: 'finance' },
  { id: 'finance-bill-list', category: 'finance' },
  { id: 'finance-financialAccount-create', category: 'finance' },
  { id: 'finance-financialAccount-list', category: 'finance' },
  { id: 'finance-invoice-create', category: 'finance' },
  { id: 'finance-invoice-get', category: 'finance' },
  { id: 'finance-invoice-list', category: 'finance' },
  { id: 'finance-payment-create', category: 'finance' },
  { id: 'finance-payment-list', category: 'finance' },

  // General tools
  { id: 'general-chart-create', category: 'general' },
  { id: 'general-dateTime', category: 'general' },
  { id: 'general-python-execute', category: 'general' },
  { id: 'general-userInput', category: 'general' },

  // Inventory tools (additional)
  { id: 'inventory-category-create', category: 'inventory' },
  { id: 'inventory-category-delete', category: 'inventory' },
  { id: 'inventory-category-get', category: 'inventory' },
  { id: 'inventory-category-list', category: 'inventory' },
  { id: 'inventory-category-update', category: 'inventory' },
  { id: 'inventory-inventoryTag-create', category: 'inventory' },
  { id: 'inventory-inventoryTag-delete', category: 'inventory' },
  { id: 'inventory-inventoryTag-get', category: 'inventory' },
  { id: 'inventory-inventoryTag-list', category: 'inventory' },
  { id: 'inventory-inventoryTag-update', category: 'inventory' },
  { id: 'inventory-item-create', category: 'inventory' },
  { id: 'inventory-item-delete', category: 'inventory' },
  { id: 'inventory-item-update', category: 'inventory' },
  { id: 'inventory-itemGroup-create', category: 'inventory' },
  { id: 'inventory-itemGroup-delete', category: 'inventory' },
  { id: 'inventory-itemGroup-get', category: 'inventory' },
  { id: 'inventory-itemGroup-list', category: 'inventory' },
  { id: 'inventory-itemGroup-update', category: 'inventory' },

  // Purchases tools
  { id: 'purchases-purchaseOrder-list', category: 'purchases' },
  { id: 'purchases-purchaseOrder-get', category: 'purchases' },
  { id: 'purchases-purchaseOrder-create', category: 'purchases' },
  { id: 'purchases-purchaseOrder-update', category: 'purchases' },

  // Sales tools (additional)
  { id: 'sales-booking-create', category: 'sales' },
  { id: 'sales-booking-get', category: 'sales' },
  { id: 'sales-booking-list', category: 'sales' },
  { id: 'sales-booking-update', category: 'sales' },
  { id: 'sales-deductionType-create', category: 'sales' },
  { id: 'sales-deductionType-delete', category: 'sales' },
  { id: 'sales-deductionType-get', category: 'sales' },
  { id: 'sales-deductionType-list', category: 'sales' },
  { id: 'sales-deductionType-update', category: 'sales' },
  { id: 'sales-load-create', category: 'sales' },
  { id: 'sales-load-get', category: 'sales' },
  { id: 'sales-load-list', category: 'sales' },
  { id: 'sales-load-update', category: 'sales' },
  { id: 'sales-loadReconciliation-delete', category: 'sales' },
  { id: 'sales-loadReconciliation-update', category: 'sales' },
  { id: 'sales-salesOrder-create', category: 'sales' },
  { id: 'sales-salesOrder-update', category: 'sales' },

  // Transportation tools
  { id: 'transportation-asset-create', category: 'transportation' },
  { id: 'transportation-asset-delete', category: 'transportation' },
  { id: 'transportation-asset-get', category: 'transportation' },
  { id: 'transportation-asset-list', category: 'transportation' },
  { id: 'transportation-asset-update', category: 'transportation' },
  { id: 'transportation-assetType-create', category: 'transportation' },
  { id: 'transportation-assetType-delete', category: 'transportation' },
  { id: 'transportation-assetType-get', category: 'transportation' },
  { id: 'transportation-assetType-list', category: 'transportation' },
  { id: 'transportation-assetType-update', category: 'transportation' },
  { id: 'transportation-containerType-create', category: 'transportation' },
  { id: 'transportation-containerType-delete', category: 'transportation' },
  { id: 'transportation-containerType-get', category: 'transportation' },
  { id: 'transportation-containerType-list', category: 'transportation' },
  { id: 'transportation-containerType-update', category: 'transportation' },
  { id: 'transportation-driver-create', category: 'transportation' },
  { id: 'transportation-driver-get', category: 'transportation' },
  { id: 'transportation-driver-list', category: 'transportation' },
  { id: 'transportation-driver-update', category: 'transportation' },
  { id: 'transportation-freightForwarder-create', category: 'transportation' },
  { id: 'transportation-freightForwarder-delete', category: 'transportation' },
  { id: 'transportation-freightForwarder-get', category: 'transportation' },
  { id: 'transportation-freightForwarder-list', category: 'transportation' },
  { id: 'transportation-freightForwarder-update', category: 'transportation' },
  { id: 'transportation-freightLine-create', category: 'transportation' },
  { id: 'transportation-freightLine-delete', category: 'transportation' },
  { id: 'transportation-freightLine-get', category: 'transportation' },
  { id: 'transportation-freightLine-list', category: 'transportation' },
  { id: 'transportation-freightLine-update', category: 'transportation' },
  { id: 'transportation-hauler-add', category: 'transportation' },
  { id: 'transportation-hauler-remove', category: 'transportation' },
  { id: 'transportation-route-create', category: 'transportation' },
  { id: 'transportation-route-get', category: 'transportation' },
  { id: 'transportation-route-list', category: 'transportation' },
  { id: 'transportation-route-update', category: 'transportation' },
  { id: 'transportation-shippingCarrier-create', category: 'transportation' },
  { id: 'transportation-shippingCarrier-delete', category: 'transportation' },
  { id: 'transportation-shippingCarrier-get', category: 'transportation' },
  { id: 'transportation-shippingCarrier-list', category: 'transportation' },
  { id: 'transportation-shippingCarrier-update', category: 'transportation' },
  { id: 'transportation-task-create', category: 'transportation' },
  { id: 'transportation-task-get', category: 'transportation' },
  { id: 'transportation-task-list', category: 'transportation' },
  { id: 'transportation-task-update', category: 'transportation' },
  { id: 'transportation-trailer-create', category: 'transportation' },
  { id: 'transportation-trailer-get', category: 'transportation' },
  { id: 'transportation-trailer-list', category: 'transportation' },
  { id: 'transportation-trailer-update', category: 'transportation' },
  {
    id: 'transportation-transportationType-create',
    category: 'transportation',
  },
  {
    id: 'transportation-transportationType-delete',
    category: 'transportation',
  },
  {
    id: 'transportation-transportationType-get',
    category: 'transportation',
  },
  {
    id: 'transportation-transportationType-list',
    category: 'transportation',
  },
  {
    id: 'transportation-transportationType-update',
    category: 'transportation',
  },
  { id: 'transportation-truck-create', category: 'transportation' },
  { id: 'transportation-truck-get', category: 'transportation' },
  { id: 'transportation-truck-list', category: 'transportation' },
  { id: 'transportation-truck-update', category: 'transportation' },
] as const;
